import { Event } from 'services/api';
import { formatDate } from 'util/formatDate';

export { formatTimeRange } from 'util/formatTime';

// Event dates are YYYY-MM-DD (no time). Format in UTC so western timezones
// don't shift the calendar day (matches event detail / util/formatDate).
export const formatLongDate = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatDate(d, {
    includeDay: true,
    includeDate: true,
    includeYear: true,
  });
};

const parseEventBoundary = (isoDate: string, time?: string): Date | null => {
  const base = new Date(isoDate);
  if (Number.isNaN(base.getTime())) return null;
  if (!time) return base;

  const match = /^(\d{1,2}):(\d{2})\s*(AM|PM)?/i.exec(time.trim());
  if (!match) return base;

  let hours = Number(match[1]);
  const minutes = Number(match[2]);
  const meridiem = match[3]?.toUpperCase();
  if (meridiem === 'PM' && hours < 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;

  const result = new Date(base);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const isHappeningNow = (
  event: Event,
  now: Date = new Date(),
): boolean => {
  if (event.status === 'CANCELLED' || event.status === 'FINISHED') return false;
  if (!event.date) return false;

  const start = parseEventBoundary(event.date, event.startTime);
  const end = parseEventBoundary(event.date, event.endTime);
  if (!start) return false;

  const startMs = start.getTime();

  const endMs = end ? end.getTime() : startMs + 24 * 60 * 60 * 1000;
  const nowMs = now.getTime();
  return nowMs >= startMs && nowMs <= endMs;
};

export const isRsvpClosed = (event: Event): boolean => {
  const types = event.ticketTypes ?? [];
  if (types.length === 0) return false;
  return types.every(t => t.reservations >= t.quantity);
};

export const localNowIso = (now: Date = new Date()): string => {
  const pad = (n: number): string => `${n}`.padStart(2, '0');
  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
  );
};

export const formatOrgCount = (count: number): string => {
  if (count > 10000) return '10,000+';
  return count.toLocaleString('en-US');
};

export const initialsOf = (name?: string): string => {
  if (!name) return '';
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
};

export const formatRelativeTime = (iso?: string): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  return `${weeks}w ago`;
};
